import { useCallback, useSyncExternalStore } from "react";

export type ToastKind = "success" | "error" | "info";

export interface ToastItem {
  id: string;
  kind: ToastKind;
  title: string;
  message?: string;
  timeoutMs: number;
}

type Listener = () => void;

let toasts: ToastItem[] = [];
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return toasts;
}

function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function pushToast(
  kind: ToastKind,
  title: string,
  message?: string,
  timeoutMs = 4200,
) {
  const item: ToastItem = { id: uid(), kind, title, message, timeoutMs };
  toasts = [item, ...toasts].slice(0, 4);
  emit();

  if (timeoutMs > 0) {
    window.setTimeout(() => dismissToast(item.id), timeoutMs);
  }

  return item.id;
}

export function dismissToast(id: string) {
  const next = toasts.filter((t) => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

export function useToasts() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const notify = useCallback(
    (kind: ToastKind, title: string, message?: string) =>
      pushToast(kind, title, message),
    [],
  );
  return { toasts: items, notify, dismiss: dismissToast };
}

export function useToast() {
  const { notify, dismiss } = useToasts();
  return { notify, dismiss };
}
